// ═══════════════════════════════════════════════════════════════
// Incident Hub — Create / Edit Incident
// Modal-style form for all incident fields
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Title, Paper, Group, Button, TextInput, Textarea, Select, MultiSelect, Text, Badge,
} from '../../components/gab/GabComponents';
import { DateInput } from '../../components/gab/MantineDates';
import { IconArrowLeft, IconMapPin, IconRoadSign, IconBuilding } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useIncidentStore } from '../../store/incidentStore';
import type { IncidentType, IncidentStatus } from '../../types';
import { WORKER_URL } from '../../lib/api-switch';
import { US_STATES, fetchStateGeo, fetchPlacesForCounties, STATE_BY_ABBR, type GeoEntry } from '../../lib/censusGeo';

const WORKER = WORKER_URL;
const HEADERS = { 'Origin': 'https://apps.opengov.com' };

interface HereSuggestion {
  id: string;
  title: string;
  resultType?: string; // 'place' | 'houseNumber' | 'street' | 'intersection' | 'locality' | ...
  address?: { label?: string };
  position?: { lat: number; lng: number };
}

// Approximate geographic centers for HERE autosuggest geographic bias
const STATE_CENTERS: Record<string, string> = {
  AL:'32.8,-86.8', AK:'64.2,-153.4', AZ:'34.3,-111.1', AR:'34.9,-92.4',
  CA:'37.2,-119.5', CO:'39.0,-105.5', CT:'41.6,-72.7', DE:'38.9,-75.5',
  FL:'28.7,-82.5', GA:'32.7,-83.4', HI:'20.3,-156.4', ID:'44.4,-114.6',
  IL:'40.0,-89.2', IN:'39.9,-86.3', IA:'42.1,-93.5', KS:'38.5,-98.4',
  KY:'37.5,-85.3', LA:'31.0,-91.8', ME:'45.4,-69.2', MD:'39.0,-76.8',
  MA:'42.3,-71.8', MI:'44.3,-85.4', MN:'46.4,-93.1', MS:'32.7,-89.7',
  MO:'38.5,-92.5', MT:'47.0,-110.0', NE:'41.5,-99.9', NV:'39.5,-117.1',
  NH:'43.7,-71.6', NJ:'40.1,-74.5', NM:'34.4,-106.1', NY:'42.9,-75.5',
  NC:'35.6,-79.8', ND:'47.5,-100.5', OH:'40.4,-82.8', OK:'35.6,-96.9',
  OR:'44.1,-120.5', PA:'40.9,-77.8', RI:'41.7,-71.5', SC:'33.9,-80.9',
  SD:'44.4,-100.2', TN:'35.9,-86.7', TX:'31.5,-99.3', UT:'39.3,-111.1',
  VT:'44.1,-72.7', VA:'37.5,-79.4', WA:'47.4,-120.5', WV:'38.6,-80.6',
  WI:'44.6,-90.0', WY:'42.9,-107.6', DC:'38.9,-77.0',
};

const TYPE_OPTIONS = [
  { value: 'natural_disaster', label: 'Natural Disaster' },
  { value: 'mci', label: 'Mass Casualty Incident' },
  { value: 'hazmat', label: 'Hazardous Materials' },
  { value: 'exercise', label: 'Exercise / Drill' },
  { value: 'training', label: 'Training' },
  { value: 'special_event', label: 'Special Event' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'closed', label: 'Closed' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/** Returns today as YYYY-MM-DD in local time */
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Strips anything after YYYY-MM-DD (e.g. timezone suffixes like "-04:00") */
function cleanDate(s: string | undefined | null): string {
  if (!s) return '';
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

export default function IncidentCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { incidents } = useAuthStore();
  const { createIncident, updateIncident, isLoading } = useIncidentStore();

  const [name, setName] = useState('');
  const [incidentType, setIncidentType] = useState<string>('natural_disaster');
  const [status, setStatus] = useState<string>('active');
  const [location, setLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [description, setDescription] = useState('');
  const [commander, setCommander] = useState('');
  const [severity, setSeverity] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(todayStr());

  // Geographic selectors
  const [stateAbbr, setStateAbbr] = useState('');
  const [counties, setCounties] = useState<GeoEntry[]>([]);
  const [allPlaces, setAllPlaces] = useState<GeoEntry[]>([]);
  const [filteredPlaces, setFilteredPlaces] = useState<GeoEntry[]>([]);
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [placesLoading, setPlacesLoading] = useState(false);

  // Fetch counties + all places when state changes.
  useEffect(() => {
    if (!stateAbbr) { setCounties([]); setAllPlaces([]); setFilteredPlaces([]); return; }
    setGeoLoading(true);
    fetchStateGeo(stateAbbr)
      .then(d => { setCounties(d.counties); setAllPlaces(d.places); setFilteredPlaces(d.places); })
      .catch(() => {
        setCounties([]);
        setAllPlaces([]);
        setFilteredPlaces([]);
        notifications.show({ message: 'Could not load county/city list — type names manually', color: 'var(--color-warning)' });
      })
      .finally(() => setGeoLoading(false));
  }, [stateAbbr]);

  // When counties are selected, narrow the city list to only places in those counties.
  useEffect(() => {
    if (!stateAbbr || selectedCounties.length === 0) {
      setFilteredPlaces(allPlaces);
      return;
    }
    const state = STATE_BY_ABBR.get(stateAbbr.toUpperCase());
    if (!state) return;
    // Look up FIPS codes for selected county names
    const fipsList = selectedCounties
      .map(name => counties.find(c => c.name === name)?.fips)
      .filter((f): f is string => !!f);
    if (!fipsList.length) { setFilteredPlaces(allPlaces); return; }
    setPlacesLoading(true);
    fetchPlacesForCounties(stateAbbr, fipsList)
      .then(setFilteredPlaces)
      .catch(() => setFilteredPlaces(allPlaces))
      .finally(() => setPlacesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCounties, stateAbbr]);

  // Address autocomplete (HERE Autosuggest — supports intersections)
  const [addrSuggestions, setAddrSuggestions] = useState<HereSuggestion[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAddressSuggestions = useCallback((query: string) => {
    if (addrTimerRef.current) clearTimeout(addrTimerRef.current);
    if (query.trim().length < 3) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }
    addrTimerRef.current = setTimeout(async () => {
      try {
        const center = STATE_CENTERS[stateAbbr?.toUpperCase() ?? ''] ?? '39.5,-98.35';
        const res = await fetch(
          `${WORKER}/api/here/autosuggest?q=${encodeURIComponent(query)}&at=${center}&limit=8`,
          { headers: HEADERS },
        );
        const data = await res.json() as { items?: HereSuggestion[] };
        const items = (data.items ?? []).filter(i => i.address?.label || i.title);
        setAddrSuggestions(items);
        setAddrOpen(items.length > 0);
      } catch {
        setAddrSuggestions([]);
        setAddrOpen(false);
      }
    }, 250);
  }, [stateAbbr]);

  const pickAddress = async (s: HereSuggestion) => {
    const label = s.address?.label || s.title;
    setLocation(label);
    setAddrOpen(false);
    setAddrSuggestions([]);
    if (s.position) {
      setLatitude(String(s.position.lat));
      setLongitude(String(s.position.lng));
      return;
    }
    // Some street/locality suggestions don't include a position — call lookup
    // by suggestion id to fetch it. Falls back silently if it fails.
    try {
      const lookup = await fetch(
        `${WORKER}/api/here/lookup?id=${encodeURIComponent(s.id)}`,
        { headers: HEADERS },
      );
      if (lookup.ok) {
        const data = await lookup.json() as { position?: { lat: number; lng: number } };
        if (data.position) {
          setLatitude(String(data.position.lat));
          setLongitude(String(data.position.lng));
        }
      }
    } catch { /* ignore — user can still set lat/lng manually */ }
  };

  // Load existing incident data for edit mode
  useEffect(() => {
    if (isEdit && id) {
      const inc = incidents.find(i => String(i.id) === id);
      if (inc) {
        setName(inc.name);
        setIncidentType(inc.incidentType);
        setStatus(inc.status);
        setLocation(inc.location);
        setLatitude(inc.latitude || '');
        setLongitude(inc.longitude || '');
        setDescription(inc.description);
        setCommander(inc.commander);
        setSeverity((inc as unknown as Record<string, unknown>).severity as string || '');
        setStartDate(cleanDate(inc.startDate));
        if (inc.state) setStateAbbr(inc.state);
        if (inc.county) setSelectedCounties(inc.county.split('|').filter(Boolean));
        if (inc.city) setSelectedCities(inc.city.split('|').filter(Boolean));
      }
    }
  }, [isEdit, id, incidents]);

  // Show a clear error if the user is trying to edit an incident that
  // doesn't exist (bad URL, deleted record, or incidents list not loaded yet)
  const editTargetMissing = isEdit && id && incidents.length > 0 && !incidents.find(i => String(i.id) === id);
  if (editTargetMissing) {
    return (
      <div>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/incidents')}>Back</Button>
            <Title order={3} c="black">Incident Not Found</Title>
          </Group>
        </Group>
        <Paper p="lg" radius="sm" withBorder>
          <Text size="sm" mb="md">
            Incident #{id} doesn't exist or you don't have permission to view it.
            It may have been deleted, or the URL may be incorrect.
          </Text>
          <Button onClick={() => navigate('/incidents')}>Back to Incident List</Button>
        </Paper>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      notifications.show({ message: 'Incident name is required', color: 'var(--color-error)' });
      return;
    }
    const dateStr = cleanDate(startDate);
    try {
      // Auto-compose location string from geo selections if not manually set
      const composedLocation = location.trim() ||
        [...selectedCities, ...selectedCounties, stateAbbr].filter(Boolean).join(', ');

      const geoPayload = {
        state: stateAbbr,
        county: selectedCounties.join('|'),
        city: selectedCities.join('|'),
        location: composedLocation,
      };

      if (isEdit && id) {
        await updateIncident(Number(id), {
          name: name.trim(),
          incidentType: incidentType as IncidentType,
          status: status as IncidentStatus,
          severity: severity,
          latitude: latitude.trim(),
          longitude: longitude.trim(),
          description: description.trim(),
          commander: commander.trim(),
          startDate: dateStr,
          ...geoPayload,
        } as any);
        notifications.show({ message: 'Incident updated', color: 'var(--color-success)' });
        navigate(`/incidents/${id}`);
      } else {
        const rid = await createIncident({
          name: name.trim(),
          incidentType: incidentType as IncidentType,
          status: status as IncidentStatus,
          severity: severity,
          latitude: latitude.trim(),
          longitude: longitude.trim(),
          description: description.trim(),
          commander: commander.trim(),
          startDate: dateStr,
          ...geoPayload,
        } as any);
        notifications.show({ message: 'Incident created', color: 'var(--color-success)' });
        navigate(rid?.id ? `/incidents/${rid.id}` : '/incidents');
      }
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to save incident', color: 'var(--color-error)' });
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate(isEdit ? `/incidents/${id}` : '/incidents')}>Back</Button>
          <Title order={3} c="black">{isEdit ? 'Edit Incident' : 'New Incident'}</Title>
        </Group>
      </Group>

      <Paper p="lg" radius="sm" withBorder>
        <TextInput label="Incident Name" required placeholder="e.g. Hurricane Milton Response"
          value={name} onChange={e => setName(e.target.value)} mb="sm" />

        <Group grow mb="sm">
          <Select label="Type" data={TYPE_OPTIONS} value={incidentType}
            onChange={v => setIncidentType(v ?? 'other')} />
          <Select label="Status" data={STATUS_OPTIONS} value={status}
            onChange={v => setStatus(v ?? 'active')} />
          <Select label="Severity" data={SEVERITY_OPTIONS} value={severity}
            onChange={v => setSeverity(v ?? '')} />
        </Group>

        <TextInput label="Incident Commander" placeholder="Name of IC"
          value={commander} onChange={e => setCommander(e.target.value)} mb="sm" />

        {/* Geographic location */}
        <Group grow mb="sm" align="flex-start">
          <Select
            label="State"
            placeholder="Select state…"
            searchable
            data={US_STATES.map(s => ({ value: s.abbr, label: `${s.name} (${s.abbr})` }))}
            value={stateAbbr}
            onChange={v => { setStateAbbr(v ?? ''); setSelectedCounties([]); setSelectedCities([]); }}
          />
          <MultiSelect
            label="County / Counties"
            placeholder={geoLoading ? 'Loading…' : stateAbbr ? 'Search counties…' : 'Select state first'}
            searchable
            disabled={!stateAbbr || geoLoading}
            data={counties.map(c => ({ value: c.name, label: c.name }))}
            value={selectedCounties}
            onChange={setSelectedCounties}
            hidePickedOptions
          />
          <MultiSelect
            label={`City / Cities${selectedCounties.length > 0 ? ` (${filteredPlaces.length} in selected ${selectedCounties.length === 1 ? 'county' : 'counties'})` : ''}`}
            placeholder={geoLoading || placesLoading ? 'Loading…' : stateAbbr ? 'Search cities…' : 'Select state first'}
            searchable
            disabled={!stateAbbr || geoLoading || placesLoading}
            data={filteredPlaces.map(p => ({ value: p.name, label: p.name }))}
            value={selectedCities}
            onChange={setSelectedCities}
            hidePickedOptions
          />
        </Group>

        <div style={{ position: 'relative' }}>
          <TextInput
            label="Specific Address or Landmark (optional)"
            placeholder='e.g. "Peachtree St & 10th Ave NE" or "Centennial Olympic Park"'
            value={location}
            onChange={e => {
              setLocation(e.target.value);
              fetchAddressSuggestions(e.target.value);
            }}
            onFocus={() => { if (addrSuggestions.length > 0) setAddrOpen(true); }}
            onBlur={() => { setTimeout(() => setAddrOpen(false), 200); }}
            description={
              latitude && longitude
                ? `Coordinates resolved: ${parseFloat(latitude).toFixed(5)}, ${parseFloat(longitude).toFixed(5)}`
                : 'Refine with a specific address or intersection. State/county/city above are used for identification.'
            }
            mb="sm"
          />
          {addrOpen && addrSuggestions.length > 0 && (
            <Paper withBorder shadow="md" style={{
              position: 'absolute', top: 'calc(100% - 18px)', left: 0, right: 0,
              zIndex: 50, maxHeight: 280, overflowY: 'auto',
            }}>
              {addrSuggestions.map(s => {
                const isIntersection = s.resultType === 'intersection';
                const isPlace = s.resultType === 'place';
                const Icon = isIntersection ? IconRoadSign : isPlace ? IconBuilding : IconMapPin;
                return (
                  <div
                    key={s.id}
                    style={{
                      padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                    }}
                    onMouseDown={e => { e.preventDefault(); pickAddress(s); }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                  >
                    <Icon size={14} style={{ marginTop: 2, color: isIntersection ? '#B12525' : isPlace ? '#0891b2' : '#6b7280' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" lineClamp={1}>{s.title}</Text>
                      {s.address?.label && s.address.label !== s.title && (
                        <Text size="xs" c="dimmed" lineClamp={1}>{s.address.label}</Text>
                      )}
                    </div>
                    {s.resultType && (
                      <Badge size="xs" variant="light"
                        color={isIntersection ? 'var(--color-error)' : isPlace ? '#03AC9E' : '#939598'}>
                        {s.resultType}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </Paper>
          )}
        </div>

        <Group grow mb="sm">
          <TextInput label="Latitude" placeholder="e.g. 28.5383"
            value={latitude} onChange={e => setLatitude(e.target.value)} />
          <TextInput label="Longitude" placeholder="e.g. -81.3792"
            value={longitude} onChange={e => setLongitude(e.target.value)} />
        </Group>

        <DateInput label="Start Date" placeholder="Pick a date"
          value={startDate}
          onChange={(v: Date | null) => {
            if (!v) { setStartDate(''); return; }
            setStartDate(`${v.getFullYear()}-${String(v.getMonth()+1).padStart(2,'0')}-${String(v.getDate()).padStart(2,'0')}`);
          }}
          clearable mb="sm" />

        <Textarea label="Description" placeholder="Describe the incident, scope, objectives..."
          value={description} onChange={e => setDescription(e.target.value)}
          minRows={4} mb="lg" />

        <Group justify="flex-end">
          <Button variant="light" onClick={() => navigate(isEdit ? `/incidents/${id}` : '/incidents')}>Cancel</Button>
          <Button onClick={handleSubmit} loading={isLoading}>
            {isEdit ? 'Save Changes' : 'Create Incident'}
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
